import { zodResolver } from '@hookform/resolvers/zod';
import CT from '@marketplace-ai/tags';
import { useSelector } from '@xstate/react';
import { useEffect, useMemo } from 'react';
import { FormProvider } from 'react-hook-form';
import { DatePicker, FormLayout, FormRow, FormSection, Select, TextInput } from '~/components';
import { useCustomForm } from '~/hooks/useCustomForm';
import { LABOR_REQ_TYPE_OPTIONS } from '../../../msaas-listing/wizard-steps/labor-metrics/constants';
import { startDateFloor } from '../../../shared-steps/startDate';
import { useJobListingActorRef } from '../../jobListingProvider';
import { emptyLaborMetricsForm } from './constants';
import { createLaborMetricsFormSchema, LaborMetricsFormValues } from './types';
import { transformApiToLaborMetricsForm } from './utils';

const TAGS = CT.employer.jobListings.laborMetricsForm;

export const LaborMetricsForm = ({ footer }: { footer: React.ReactNode }) => {
  const jobListingRef = useJobListingActorRef();
  const { laborMetricsRef, listing } = useSelector(jobListingRef, (state) => state.context);

  // [MCV-1524] The start date already on the record is allowed to stay in the
  // past; only *moving* it into the past is rejected. Memoised so the resolver
  // identity is stable across renders.
  const persistedStartDate = listing?.detail?.startDate;
  const schema = useMemo(
    () => createLaborMetricsFormSchema(persistedStartDate),
    [persistedStartDate]
  );

  const methods = useCustomForm<LaborMetricsFormValues>({
    //@ts-expect-error typing for preprocess parsing does not work well
    resolver: zodResolver(schema),
    defaultValues: listing?.detail?.id
      ? transformApiToLaborMetricsForm(listing)
      : emptyLaborMetricsForm,
  });

  const {
    handleSubmit,
    watch,
    setValue,
    formState: { isDirty },
  } = methods;

  useEffect(() => {
    if (listing) {
      setValue('id', listing.detail?.id);
      setValue('jobListingId', listing.id);
    }
  }, [listing]);

  const laborReqType = watch('laborReqType').value;

  useEffect(() => {
    if (laborReqType !== 'RVU') {
      setValue('numRvusReq', null);
    }
  }, [laborReqType]);

  const onSubmitSuccess = (values: LaborMetricsFormValues) => {
    const existingDetail = listing?.detail?.id;
    if (existingDetail && isDirty) {
      laborMetricsRef.send({ type: 'ON_UPDATE', payload: { values } });
    } else if (!existingDetail) {
      laborMetricsRef.send({ type: 'ON_ADD', payload: { values } });
    } else {
      jobListingRef.send({ type: 'ON_NEXT' });
    }
  };

  const onSubmitError = (err: unknown) => {
    console.log('Labor Metrics Info Form Submit Error: ', err, methods.getValues());
  };

  const numOfShifts = watch('numOfShifts');
  const hoursPerShift = watch('hoursPerShift');
  const minRvusPerShift = watch('minRvusPerShift') || 0;
  const numRvusReq = watch('numRvusReq') || 0;

  const totalShifts =
    laborReqType === 'RVU'
      ? minRvusPerShift
        ? Math.ceil(numRvusReq / minRvusPerShift)
        : null
      : numOfShifts;

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmitSuccess, onSubmitError)}>
        <FormLayout>
          <h3 className="sub-heading-sb">Contract Duration</h3>
          <FormSection componentsPerLine={1}>
            <FormRow>
              <Select
                name={'laborReqType'}
                cyTag={TAGS.laborReqType}
                label={'Labor Requirements Type'}
                options={LABOR_REQ_TYPE_OPTIONS}
              />
            </FormRow>
          </FormSection>
          <FormSection componentsPerLine={2}>
            <FormRow>
              <DatePicker
                name={'startDate'}
                cyTag={TAGS.startDate}
                label={'Start Date'}
                // [MCV-1524] Floor the picker at today unless the record
                // already carries an earlier date, which must stay selectable
                // so an in-flight listing can still be edited.
                minDate={startDateFloor(persistedStartDate)}
                required
              />
            </FormRow>
            <FormRow>
              <DatePicker name={'endDate'} cyTag={TAGS.endDate} label={'End Date'} />
            </FormRow>
          </FormSection>
          <h3 className="sub-heading-sb">Labor Requirements</h3>
          <FormSection componentsPerLine={3}>
            {laborReqType === 'RVU' && (
              <FormRow>
                <TextInput
                  name={'numRvusReq'}
                  cyTag={TAGS.numRvusReq}
                  label={'Total RVUs Required'}
                  description="Relative Value Units needed"
                  type="number"
                  required={laborReqType === 'RVU'}
                />
              </FormRow>
            )}
            <FormRow>
              <TextInput
                name={'numOfShifts'}
                cyTag={TAGS.numOfShifts}
                label={'Shifts'}
                description="Default: 200 shifts/year"
                type="number"
                required
              />
            </FormRow>
            <FormRow>
              <TextInput
                name={'hoursPerShift'}
                cyTag={TAGS.hoursPerShift}
                label={'Hours per Shift'}
                description="Default: 8 hours"
                type="number"
                required
              />
            </FormRow>
            <FormRow>
              <TextInput
                name={'minRvusPerShift'}
                cyTag={TAGS.minRvusPerShift}
                label={'Minimum RVUs per Shift'}
                type="number"
                required={laborReqType === 'RVU'}
                description={
                  laborReqType === 'RVU' ? undefined : 'Optional - enables $/RVU comparison'
                }
              />
            </FormRow>
          </FormSection>
          <div className="w-full bg-primary-t-900 p-4 rounded-lg">
            <p className="body-text-sb mb-3">Calculated Totals: </p>
            <ul className="body-text list-disc list-inside">
              <li className="mb-2" data-cy={TAGS.totalShifts}>
                Total Shifts:{' '}
                {laborReqType === 'RVU' ? (
                  <>
                    {numRvusReq && minRvusPerShift ? (
                      <>
                        <b className="font-bold body-text-sb">{totalShifts}</b> ({numRvusReq} RVUs
                        Required / {minRvusPerShift} minimum RVUs per shift)
                      </>
                    ) : (
                      <b className="font-bold body-text-sb">--</b>
                    )}
                  </>
                ) : (
                  <>
                    <b className="font-bold body-text-sb">{numOfShifts}</b> ({numOfShifts} shifts)
                  </>
                )}
              </li>
              <li className="mb-2" data-cy={TAGS.totalHours}>
                Total Hours:{' '}
                {totalShifts && hoursPerShift ? (
                  <>
                    <b className="font-bold body-text-sb">{totalShifts * hoursPerShift} </b>(
                    {totalShifts} shifts * {hoursPerShift} hours)
                  </>
                ) : (
                  <b className="font-bold body-text-sb">--</b>
                )}
              </li>
            </ul>
          </div>
          {footer}
        </FormLayout>
      </form>
    </FormProvider>
  );
};
